import React, { useState } from 'react';
import { CalendarWeek, CheckCircleFill, LockFill } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { Timeline, TimelineItemProps, Typography } from 'antd';

import Progress from '@/components/shared/Progress';
import { useModuleProgress } from '@/hooks/useProgress';
import { enStatus, IModule } from '@/interfaces/course';
import styled, { css, cx, StyledProp } from '@/styled';

const iconMapper: Record<string, any> = {
  [enStatus.EXPIRED]: <LockFill size={16} />,
  [enStatus.NOT_RELEASED]: <CalendarWeek size={16} />,
  [enStatus.NOT_PURCHASED]: <LockFill size={16} />
};

const ModuleTimeline = ({ module, className }: { module: IModule } & StyledProp) => {
  const { id } = useParams();
  const isCurrentModule = Boolean(module.lessons.find(lesson => lesson.id.toString() === id));
  const [expand, setExpand] = useState(isCurrentModule);
  const moduleProgress = useModuleProgress(module.id);
  const navigate = useNavigate();

  const expandModule = () => {
    setExpand(show => !show);
  };

  const items: TimelineItemProps[] = [
    {
      dot: <Progress type='circle' showInfo={false} percent={moduleProgress} strokeWidth={5} size={28} />,
      children: (
        <>
          <Typography.Title level={3} data-current={isCurrentModule} onClick={expandModule}>
            {module.title}
          </Typography.Title>
          <Typography.Text data-show={!expand || moduleProgress === 0} data-current={isCurrentModule} className='total'>
            {module.lessons.length + ' aulas'}
          </Typography.Text>
        </>
      )
    }
  ];

  if (expand) {
    module.lessons.forEach(lesson => {
      items.push({
        dot: lesson.watched ? <CheckCircleFill size={16} /> : iconMapper[lesson.status] ?? <div className='dot' />,
        children: (
          <Typography.Paragraph
            data-current={lesson.id.toString() === id}
            onClick={() => navigate(`/curso/${lesson.hash_course}/aula/${lesson.id}`)}
            ellipsis={{ rows: 2 }}
          >
            {lesson.title}
          </Typography.Paragraph>
        )
      });
    });
  }

  return <Timeline rootClassName={cx(className, isCurrentModule && 'currentModule')} items={items} />;
};
export default styled(ModuleTimeline)(
  ({ theme }) => css`
    .total {
      display: none;

      &[data-show='true'] {
        display: block;
      }
    }

    .dot {
      border: 1px solid ${theme.palette.gray[8]};
      border-radius: 100%;
      width: 16px;
      height: 16px;
      margin: 0;
    }

    .ant-typography {
      color: ${theme.colorTextTertiary};
      margin: 0;
    }

    div.ant-typography {
      cursor: pointer;
    }

    h3.ant-typography {
      line-height: 100%;
      cursor: pointer;
    }

    .ant-typography[data-current='true'] {
      color: ${theme.colorText};
    }

    .ant-timeline-item-head-custom {
      padding-block: 0;
    }

    .ant-timeline-item-head {
      background-color: transparent;
    }

    .ant-timeline-item-tail {
      inset-block-start: 13px;
      height: calc(100% - 16px);
    }

    > li:first-child > div:first-child {
      inset-block-start: 18px !important;
      height: calc(100% - 22px) !important;
    }

    .ant-timeline-item .ant-timeline-item-content {
      margin-inline-start: ${theme.space.pxToRem(32)};
    }
  `
);
